import { useMemo, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useAcList, useAcMutations, useRevokePass, type AcPass, type PassStatus } from "../api/accessControlApi";
import {
  ConsolePage,
  DetailRow,
  Drawer,
  FilterSelect,
  Initials,
  KpiRow,
  KpiTile,
  LoadState,
  PASS_TONE,
  Panel,
  SearchBox,
  StatusChip,
  ToolButton,
  formatDateTime,
  relativeTime,
  toneOf,
  useTicker,
} from "../components/console/Console";
import FormModal, { toLocalInput, type FormField, type FormValues } from "../components/console/FormModal";
import PassInvitation from "../components/PassInvitation";

// ===========================================================================
// Accesos temporales → Pases (y Control de acceso → Invitaciones y QR):
// lista por estado, alta con QR descargable e invitación compartible, ficha
// con revocación. `history` muestra sólo los usados, vencidos y revocados.
// ===========================================================================

const ALL: PassStatus[] = ["active", "scheduled", "used", "expired", "revoked"];
const HISTORY: PassStatus[] = ["used", "expired", "revoked"];

/** Comprime la foto de identificación a ≤ 480 px (JPEG) para guardarla embebida. */
export async function compressPhoto(file: File, maxSide = 480): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(img.naturalWidth * scale);
    c.height = Math.round(img.naturalHeight * scale);
    c.getContext("2d")?.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL("image/jpeg", 0.72);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function PassesConsole({ history = false }: { history?: boolean }) {
  const { t, i18n } = useTranslation("accessConsole");
  const now = useTicker();
  const passesQ = useAcList("passes", undefined, { refetchInterval: 60_000 });
  const sitesQ = useAcList("sites");
  const pointsQ = useAcList("points");
  const { create } = useAcMutations("passes");
  const revoke = useRevokePass();

  const statuses = history ? HISTORY : ALL;
  const [tab, setTab] = useState<PassStatus | "all">(history ? "all" : "active");
  const [search, setSearch] = useState("");
  const [site, setSite] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [invite, setInvite] = useState<AcPass | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);

  const passes = useMemo(() => (passesQ.data ?? []).filter((p) => statuses.includes(p.effective_status)), [passesQ.data, statuses]);
  const counts = Object.fromEntries(ALL.map((s) => [s, passes.filter((p) => p.effective_status === s).length])) as Record<PassStatus, number>;
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return passes
      .filter((p) => (tab === "all" || p.effective_status === tab) && (!site || p.site_id === site) && (!q || `${p.visitor_name} ${p.visitor_company ?? ""} ${p.host_name} ${p.code} ${p.vehicle_plate ?? ""}`.toLowerCase().includes(q)))
      .sort((a, b) => (tab === "scheduled" ? Date.parse(a.valid_from) - Date.parse(b.valid_from) : Date.parse(b.valid_from) - Date.parse(a.valid_from)));
  }, [passes, tab, site, search]);
  const current = (passesQ.data ?? []).find((p) => p.id === openId) ?? null;
  const pointName = (id: string) => (pointsQ.data ?? []).find((p) => p.id === id)?.name ?? id;

  const fields: FormField[] = [
    { key: "visitor_name", label: t("fields.visitor"), required: true },
    { key: "visitor_company", label: t("fields.company") },
    { key: "visitor_phone", label: t("fields.phone"), placeholder: "+52 55 0000 0000" },
    { key: "visitor_email", label: t("fields.email") },
    { key: "host_name", label: t("fields.host"), required: true },
    { key: "host_unit", label: t("fields.hostUnit"), placeholder: t("passes.hostUnitPlaceholder") },
    { key: "reason", label: t("fields.reason"), full: true },
    { key: "site_id", label: t("fields.site"), type: "select", required: true, options: (sitesQ.data ?? []).map((s) => ({ value: s.id, label: s.name })) },
    { key: "vehicle_plate", label: t("fields.plate"), placeholder: "ABC-123-D" },
    { key: "valid_from", label: t("fields.validFrom"), type: "datetime", required: true },
    { key: "valid_until", label: t("fields.validUntil"), type: "datetime", required: true },
    { key: "max_uses", label: t("fields.maxUses"), type: "number", required: true, min: 1, max: 999 },
    { key: "point_ids", label: t("fields.allowedPoints"), type: "multiselect", options: (pointsQ.data ?? []).map((p) => ({ value: p.id, label: `${p.name}` })), hint: t("passes.pointsHint") },
    { key: "notes", label: t("fields.notes"), type: "textarea" },
  ];

  const onPhoto = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      setPhoto(await compressPhoto(f));
    } catch {
      toast.error(t("passes.photoError"));
    }
  };

  const submit = async (v: FormValues) => {
    const from = new Date(String(v.valid_from));
    const until = new Date(String(v.valid_until));
    if (!(until > from)) {
      toast.error(t("passes.untilAfterFrom"));
      return;
    }
    try {
      const created = await create.mutateAsync({
        ...(v as Partial<AcPass>),
        valid_from: from.toISOString(),
        valid_until: until.toISOString(),
        max_uses: Number(v.max_uses || 1),
        id_photo_url: photo,
      });
      setCreating(false);
      setPhoto(null);
      setInvite(created);
      toast.success(t("passes.created"));
    } catch {
      toast.error(t("common.saveError"));
    }
  };

  const doRevoke = async (p: AcPass) => {
    if (!window.confirm(t("passes.confirmRevoke", { interpolation: { escapeValue: false }, name: p.visitor_name }))) return;
    try {
      await revoke.mutateAsync(p.id);
      toast.success(t("passes.revoked"));
    } catch {
      toast.error(t("common.saveError"));
    }
  };

  const start = new Date(Math.ceil(Date.now() / 900_000) * 900_000);

  return (
    <ConsolePage
      icon={history ? "ri-history-line" : "ri-qr-code-line"}
      title={history ? t("passes.historyTitle") : t("passes.title")}
      subtitle={history ? t("passes.historySubtitle") : t("passes.subtitle")}
      actions={!history && <ToolButton icon="ri-add-line" variant="primary" onClick={() => setCreating(true)}>{t("passes.new")}</ToolButton>}
    >
      <KpiRow>
        {statuses.map((s) => (
          <KpiTile key={s} icon={s === "active" ? "ri-checkbox-circle-line" : s === "scheduled" ? "ri-calendar-event-line" : s === "used" ? "ri-door-open-line" : s === "expired" ? "ri-time-line" : "ri-forbid-line"} tone={toneOf(PASS_TONE, s)} label={t(`passStatus.${s}`)} value={counts[s]} />
        ))}
        <KpiTile icon="ri-qr-code-line" tone="neutral" label={t("passes.total")} value={passes.length} />
      </KpiRow>

      <div className="flex flex-wrap items-center gap-2">
        <div role="tablist" aria-label={t("fields.status")} className="inline-flex flex-wrap overflow-hidden rounded-lg border border-line bg-card">
          {(["all", ...statuses] as const).map((s) => (
            <button key={s} type="button" role="tab" aria-selected={tab === s} onClick={() => setTab(s)} className={`px-3 py-1.5 text-sm ${tab === s ? "bg-primary-500 text-on-primary" : "text-content-secondary hover:bg-subtle"}`}>
              {s === "all" ? t("common.all") : t(`passStatus.${s}`)}
              <span className="ml-1 text-xs opacity-75">{s === "all" ? passes.length : counts[s]}</span>
            </button>
          ))}
        </div>
        <SearchBox value={search} onChange={setSearch} placeholder={t("passes.searchPlaceholder")} />
        <FilterSelect label={t("fields.site")} value={site} onChange={setSite} options={[{ value: "", label: t("common.allSites") }, ...(sitesQ.data ?? []).map((s) => ({ value: s.id, label: s.name }))]} />
      </div>

      <Panel bodyClassName="overflow-x-auto">
        <LoadState loading={passesQ.isLoading} error={passesQ.error} empty={!rows.length} emptyText={t("passes.empty")} onRetry={() => passesQ.refetch()}>
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-subtle text-left text-[11px] uppercase tracking-wide text-content-muted">
              <tr>
                <th className="px-3 py-2">{t("fields.visitor")}</th>
                <th className="px-3 py-2">{t("fields.host")}</th>
                <th className="px-3 py-2">{t("fields.site")}</th>
                <th className="px-3 py-2">{t("fields.validity")}</th>
                <th className="px-3 py-2">{t("fields.uses")}</th>
                <th className="px-3 py-2">{t("fields.status")}</th>
                <th className="px-3 py-2 text-right">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((p) => (
                <tr key={p.id} onClick={() => setOpenId(p.id)} className="cursor-pointer hover:bg-subtle">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2.5">
                      {p.id_photo_url ? <img src={p.id_photo_url} alt="" className="h-9 w-9 rounded-full object-cover" /> : <Initials name={p.visitor_name} />}
                      <div className="min-w-0">
                        <div className="truncate font-medium text-content">{p.visitor_name}</div>
                        <div className="truncate text-xs text-content-secondary">{p.visitor_company ?? "—"} · <span className="font-mono">{p.code}</span></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-content-secondary"><div className="text-content">{p.host_name}</div><div className="text-xs">{p.host_unit}</div></td>
                  <td className="px-3 py-2 text-content-secondary">{p.site_name ?? "—"}{p.vehicle_plate && <div className="font-mono text-[11px]"><i className="ri-car-line mr-1" />{p.vehicle_plate}</div>}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-content-secondary">
                    <div>{formatDateTime(p.valid_from, i18n.language)}</div>
                    <div>→ {formatDateTime(p.valid_until, i18n.language)}</div>
                  </td>
                  <td className="px-3 py-2 tabular-nums text-content-secondary">{p.uses_count} / {p.max_uses}</td>
                  <td className="px-3 py-2"><StatusChip tone={toneOf(PASS_TONE, p.effective_status)}>{t(`passStatus.${p.effective_status}`)}</StatusChip></td>
                  <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <button type="button" title={t("invitation.title")} aria-label={t("invitation.title")} onClick={() => setInvite(p)} className="rounded p-1.5 text-primary-500 hover:bg-primary-500/10"><i className="ri-qr-code-line" /></button>
                    {(p.effective_status === "active" || p.effective_status === "scheduled") && (
                      <button type="button" title={t("passes.revoke")} aria-label={t("passes.revoke")} onClick={() => doRevoke(p)} className="rounded p-1.5 text-error hover:bg-error/10"><i className="ri-forbid-line" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </LoadState>
      </Panel>

      <Drawer
        open={Boolean(current)}
        title={current?.visitor_name ?? ""}
        subtitle={current ? `${current.code} · ${current.visitor_company ?? ""}` : undefined}
        onClose={() => setOpenId(null)}
        footer={
          current && (
            <>
              {(current.effective_status === "active" || current.effective_status === "scheduled") && <ToolButton icon="ri-forbid-line" variant="danger" onClick={() => doRevoke(current)}>{t("passes.revoke")}</ToolButton>}
              <ToolButton icon="ri-share-forward-line" variant="primary" onClick={() => setInvite(current)}>{t("invitation.open")}</ToolButton>
            </>
          )
        }
      >
        {current && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              {current.id_photo_url ? <img src={current.id_photo_url} alt={t("fields.idPhoto")} className="h-14 w-14 rounded-full object-cover" /> : <Initials name={current.visitor_name} size="lg" />}
              <div>
                <StatusChip tone={toneOf(PASS_TONE, current.effective_status)}>{t(`passStatus.${current.effective_status}`)}</StatusChip>
                <p className="mt-1 text-xs text-content-secondary">{t("passes.startsEnds", { interpolation: { escapeValue: false }, from: relativeTime(current.valid_from, i18n.language, now), until: relativeTime(current.valid_until, i18n.language, now) })}</p>
              </div>
            </div>
            <div>
              <DetailRow label={t("fields.host")}>{current.host_name}{current.host_unit ? ` · ${current.host_unit}` : ""}</DetailRow>
              <DetailRow label={t("fields.reason")}>{current.reason || "—"}</DetailRow>
              <DetailRow label={t("fields.site")}>{current.site_name ?? "—"}</DetailRow>
              <DetailRow label={t("fields.allowedPoints")}>{(current.point_ids ?? []).length ? (current.point_ids ?? []).map(pointName).join(", ") : t("passes.anyPoint")}</DetailRow>
              <DetailRow label={t("fields.validFrom")}>{formatDateTime(current.valid_from, i18n.language)}</DetailRow>
              <DetailRow label={t("fields.validUntil")}>{formatDateTime(current.valid_until, i18n.language)}</DetailRow>
              <DetailRow label={t("fields.uses")}>{current.uses_count} / {current.max_uses}</DetailRow>
              <DetailRow label={t("fields.lastUse")}>{formatDateTime(current.last_used_at, i18n.language)}</DetailRow>
              <DetailRow label={t("fields.plate")}>{current.vehicle_plate ?? "—"}</DetailRow>
              <DetailRow label={t("fields.phone")}>{current.visitor_phone ?? "—"}</DetailRow>
              {current.revoked_at && <DetailRow label={t("passStatus.revoked")}>{formatDateTime(current.revoked_at, i18n.language)}</DetailRow>}
              {current.notes && <DetailRow label={t("fields.notes")}>{current.notes}</DetailRow>}
            </div>
          </div>
        )}
      </Drawer>

      {creating && (
        <FormModal
          title={t("passes.new")}
          fields={fields}
          initial={{ valid_from: toLocalInput(start), valid_until: toLocalInput(new Date(start.getTime() + 4 * 3600_000)), max_uses: 1, site_id: sitesQ.data?.[0]?.id ?? "", point_ids: [] }}
          submitLabel={t("passes.createAndShare")}
          busy={create.isPending}
          onSubmit={submit}
          onClose={() => { setCreating(false); setPhoto(null); }}
          extra={
            <div className="flex items-center gap-3 rounded-lg border border-dashed border-line p-3 text-sm">
              {photo ? <img src={photo} alt={t("fields.idPhoto")} className="h-14 w-14 rounded object-cover" /> : <i className="ri-id-card-line text-2xl text-content-muted" />}
              <label className="cursor-pointer text-primary-500 hover:underline">
                {photo ? t("passes.changePhoto") : t("passes.addPhoto")}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={onPhoto} />
              </label>
              <span className="text-xs text-content-muted">{t("passes.photoHint")}</span>
              {photo && <button type="button" onClick={() => setPhoto(null)} className="ml-auto text-xs text-error">{t("common.remove")}</button>}
            </div>
          }
        />
      )}
      {invite && <PassInvitation pass={invite} onClose={() => setInvite(null)} />}
    </ConsolePage>
  );
}
